import React, { useState } from 'react';
import {
  Alert, Badge, Button, EmptyState, Modal, Table, TextInput,
} from '@opendate-app/setlist';

const KIND_LABELS = { child: 'Child' };

const noteFor = (guest) => (guest.birth_year ? `Age ${new Date().getFullYear() - guest.birth_year}` : '—');

function HouseholdGuests({ household, addGuest, removeGuest }) {
  const allowance = household.authorized_guests;
  const blankForm = { name: '', kind: 'child', birth_year: '' };

  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [guestToRemove, setGuestToRemove] = useState(null);

  const openForm = () => {
    setForm(blankForm);
    setErrors([]);
  };

  const closeForm = () => {
    if (!submitting) setForm(null);
  };

  const submit = async () => {
    setSubmitting(true);
    const attrs = { name: form.name, kind: form.kind };
    if (form.birth_year !== '') attrs.birth_year = form.birth_year;
    const messages = await addGuest(household, attrs);
    setSubmitting(false);
    if (messages.length) setErrors(messages);
    else setForm(null);
  };

  const confirmRemove = async () => {
    setSubmitting(true);
    await removeGuest(household, guestToRemove);
    setSubmitting(false);
    setGuestToRemove(null);
  };

  return (
    <div className="border-top mt-3 pt-3">
      <div className="d-flex justify-content-between align-items-center">
        <h3 className="h6 mb-0">Authorized guests</h3>
        <Button variant="secondary" size="sm" onClick={openForm}>+ Add guest</Button>
      </div>
      <p className="text-muted small mt-1 mb-2">
        No login — checked in by staff onsite. Children enter with the membership. A caregiver gets their own login as a household member.
      </p>

      <Table
        columns={[
          { key: 'name', header: 'Guest', primary: true, render: (guest) => guest.name },
          {
            key: 'kind',
            header: 'Type',
            width: '180px',
            render: (guest) => <Badge color="gray" size="sm" dot>{KIND_LABELS[guest.kind]}</Badge>,
          },
          { key: 'notes', header: 'Notes', render: (guest) => <span className="text-muted">{noteFor(guest)}</span> },
          {
            key: 'actions',
            header: '',
            align: 'right',
            width: '48px',
            render: (guest) => (
              <button type="button" className="cdp-icon-btn-danger" onClick={() => setGuestToRemove(guest)} aria-label={`Remove ${guest.name}`}>
                <i className="fal fa-times" aria-hidden="true" />
              </button>
            ),
          },
        ]}
        data={allowance.guests}
        getRowKey={(guest) => guest.id}
        empty={<EmptyState size="sm" title="No guests yet." />}
      />

      <Modal
        open={!!form}
        onClose={closeForm}
        title={`Add a guest to ${household.name || 'this household'}`}
        footer={(
          <>
            <Button variant="secondary" onClick={closeForm} disabled={submitting}>Cancel</Button>
            <Button variant="primary" onClick={submit} disabled={submitting || !form?.name.trim()}>Add guest</Button>
          </>
        )}
      >
        {form && (
          <div className="d-flex flex-column" style={{ gap: 16 }}>
            {errors.length > 0 && <Alert color="error" role="alert" title={errors.join(' ')} />}
            <TextInput
              label="Name"
              name="guest_name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextInput
              label="Birth year"
              name="guest_birth_year"
              type="number"
              min={1900}
              max={new Date().getFullYear()}
              hint="Optional. Shown to staff as an approximate age."
              value={form.birth_year}
              onChange={(e) => setForm({ ...form, birth_year: e.target.value })}
            />
          </div>
        )}
      </Modal>

      <Modal
        open={!!guestToRemove}
        onClose={() => !submitting && setGuestToRemove(null)}
        type="error"
        title={`Remove ${guestToRemove?.name || 'guest'}?`}
        description="They will no longer be checked in with this membership."
        footer={(
          <>
            <Button variant="secondary" onClick={() => setGuestToRemove(null)} disabled={submitting}>Cancel</Button>
            <Button variant="destructive" onClick={confirmRemove} disabled={submitting}>Remove</Button>
          </>
        )}
      />
    </div>
  );
}

export default HouseholdGuests;
